import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Card, Divider, Icon, Message, Typography } from '@uigovpe/components';
import { PageHeader } from '@/shared/components/PageHeader';
import { Skeleton } from '@/shared/components/Skeleton';
import { ErrorNotice } from '@/shared/components/ErrorNotice';
import { StatusTag } from '@/shared/components/StatusTag';
import { LabeledValue } from '@/shared/components/LabeledValue';
import { messageFromError } from '@/shared/api/apiError';
import { CertificateRequest, Option } from '@/shared/types/certificate-request';
import { formatDate, formatDateTime, formatFullAddress } from '@/shared/utils/formatting';
import { certificateRequestApi } from '../certificate-request/api/certificateRequestApi';
import { referenceDataApi } from '../certificate-request/api/referenceDataApi';
import { RequestTimeline } from './components/RequestTimeline';

export function RequestDetailsPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [request, setRequest] = useState<CertificateRequest | null>(null);
  const [incidentTypes, setIncidentTypes] = useState<Option[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    Promise.all([certificateRequestApi.getById(id), referenceDataApi.load()])
      .then(([data, referenceData]) => {
        if (!active) return;
        setRequest(data);
        setIncidentTypes(referenceData.incidentTypes);
      })
      .catch((e) => active && setError(messageFromError(e, 'Solicitação não encontrada.')))
      .finally(() => active && setLoading(false));

    return () => {
      active = false;
    };
  }, [id]);

  const handleDownloadPdf = async () => {
    setDownloadError(null);
    setDownloadingPdf(true);
    try {
      const blob = await certificateRequestApi.downloadCertificatePdf(id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `certidao-${request?.protocol ?? id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setDownloadError(messageFromError(e, 'Não foi possível baixar o PDF da certidão.'));
    } finally {
      setDownloadingPdf(false);
    }
  };

  const incidentTypeLabel =
    incidentTypes.find((type) => type.value === request?.incidentType)?.label ??
    request?.incidentType;

  const header = (
    <PageHeader
      eyebrow="Certidão Digital"
      title={request?.protocol ?? 'Rascunho'}
      titleLoading={loading}
      description="Acompanhe o andamento da sua solicitação."
      onBack={() => navigate('/my-requests')}
    />
  );

  if (loading) {
    return (
      <>
        {header}
        <div className="cd-details__status">
          <Skeleton width="6rem" height="1.5rem" borderRadius="999px" />
          <Skeleton width="10rem" height="1rem" />
        </div>
        <Card className="cd-details__card">
          <Skeleton width="8rem" height="1.5rem" />
          {Array.from({ length: 6 }, (_, i) => (
            <div className="cd-labeled-value" key={i}>
              <Skeleton width="6rem" height="1rem" />
              <Skeleton width="70%" height="1rem" />
            </div>
          ))}
        </Card>
        <section className="cd-details__timeline">
          <Skeleton width="8rem" height="1.5rem" />
          <ol className="cd-timeline">
            {Array.from({ length: 2 }, (_, i) => (
              <li className="cd-timeline__item" key={i}>
                <span className="cd-timeline__marker" aria-hidden />
                <div className="cd-timeline__content">
                  <Skeleton width="8rem" height="1rem" />
                  <Skeleton width="6rem" height="0.85rem" />
                </div>
              </li>
            ))}
          </ol>
        </section>
      </>
    );
  }
  if (error || !request) {
    return (
      <>
        {header}
        <ErrorNotice message={error ?? 'Solicitação não encontrada.'} />
      </>
    );
  }

  return (
    <>
      {header}

      <div className="cd-details__status">
        <StatusTag status={request.status} />
        <Typography variant="span" size="sm">
          Atualizada em {formatDateTime(request.updatedAt)}
        </Typography>
      </div>

      {request.statusReason && (
        <Message
          severity={request.status === 'REJECTED' ? 'error' : 'warn'}
          text={request.statusReason}
        />
      )}

      {request.status === 'COMPLETED' && (
        <Card className="cd-details__card">
          <div className="cd-details__certificate-header">
            <div className="cd-details__certificate-title">
              <Icon icon="description" />
              <Typography variant="h2" size="default" fontWeight="bold">
                Certidão de Ocorrência
              </Typography>
            </div>
            <Button
              label="Baixar certidão (PDF)"
              icon={<Icon icon="download" />}
              loading={downloadingPdf}
              onClick={handleDownloadPdf}
            />
          </div>
          {downloadError && <Message severity="error" text={downloadError} />}
        </Card>
      )}

      <Card className="cd-details__card">
        <Typography variant="h2" size="default" fontWeight="bold">
          Ocorrência
        </Typography>
        <LabeledValue label="Órgão" value="Corpo de Bombeiros Militar de Pernambuco" />
        <LabeledValue label="Natureza do fato" value={incidentTypeLabel} />
        <LabeledValue label="Data" value={formatDate(request.incidentDate)} />
        <LabeledValue label="Horário" value={request.incidentTime} />
        <LabeledValue label="Local" value={formatFullAddress(request.location)} />
        <LabeledValue label="Descrição" value={request.description} />
      </Card>

      <section className="cd-details__timeline">
        <Typography variant="h2" size="default" fontWeight="bold">
          Andamento
        </Typography>
        <RequestTimeline events={request.timeline ?? []} />
      </section>
    </>
  );
}
